import { apiGet, apiPost, apiUpload, type JsonMap } from '@/api/client';

function q(params?: Record<string, string | number | undefined | null>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** Auth */
export const auth = {
  login: (body: JsonMap) => apiPost('/auth/login', body),
  register: (body: JsonMap) => apiPost('/auth/register', body),
  logout: () => apiPost('/auth/logout'),
  me: () => apiGet('/me'),
};

/** Account */
export const account = {
  changePassword: (body: JsonMap) => apiPost('/account/change-password', body),
  sessions: () => apiGet('/account/sessions'),
  revokeSession: (body: JsonMap) => apiPost('/account/sessions/revoke', body),
  transactionPin: () => apiGet('/account/transaction-pin'),
  setTransactionPin: (body: JsonMap) => apiPost('/account/transaction-pin/set', body),
  removeTransactionPin: (body: JsonMap) => apiPost('/account/transaction-pin/remove', body),
  apiInfo: () => apiGet('/account/api'),
  regenerateApi: () => apiPost('/account/api/regenerate'),
  pendingOrders: () => apiGet('/account/api/pending-orders'),
  submitPending: (body: JsonMap) => apiPost('/account/api/pending-orders/submit', body),
  cancelPending: (body: JsonMap) => apiPost('/account/api/pending-orders/cancel', body),
};

/** Content / home */
export const content = {
  slides: (placement?: string) =>
    apiGet(`/content/slides${placement ? q({ placement }) : ''}`),
  siteStatus: () => apiGet('/site/status'),
};

/** Customer recharge */
export const customerRecharge = {
  packages: () => apiGet('/customer/recharge/packages'),
  order: (body: JsonMap, idempotencyKey?: string) =>
    apiPost(
      '/customer/recharge/order',
      body,
      idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    ),
};

/** Products */
export const products = {
  content: (params?: Record<string, string | number>) =>
    apiGet(`/products/content${q(params)}`),
  item: (id: string | number) => apiGet(`/products/item${q({ id })}`),
  order: (body: JsonMap) => apiPost('/products/order', body),
  orders: (params?: Record<string, string | number>) => apiGet(`/products/orders${q(params)}`),
};

/** Bills */
export const bills = {
  content: () => apiGet('/bills/content'),
  item: (id: string | number) => apiGet(`/bills/item${q({ id })}`),
  order: (body: JsonMap) => apiPost('/bills/order', body),
  orders: (params?: Record<string, string | number>) => apiGet(`/bills/orders${q(params)}`),
};

/** Wallet / payments / deposits */
export const wallet = {
  transactions: (params?: Record<string, string | number>) =>
    apiGet(`/wallet/transactions${q(params)}`),
  paymentMethods: (walletKey?: string) =>
    apiGet(`/payment/methods${walletKey ? q({ wallet: walletKey }) : ''}`),
  depositRequest: (body: JsonMap) => apiPost('/deposit/request', body),
  orders: (params?: Record<string, string | number>) => apiGet(`/orders${q(params)}`),
};

/** Support */
export const support = {
  tickets: (params?: Record<string, string | number>) => apiGet(`/support/tickets${q(params)}`),
  createTicket: (body: JsonMap) => apiPost('/support/tickets', body),
  messages: (ticketId: string | number) => apiGet(`/support/messages${q({ ticket_id: ticketId })}`),
  reply: (body: JsonMap) => apiPost('/support/reply', body),
  close: (body: JsonMap) => apiPost('/support/close', body),
};

/** KYC — submit expects multipart FormData with image files */
export const kyc = {
  me: () => apiGet('/kyc/me'),
  submit: (body: FormData) => apiUpload('/kyc/submit', body),
};

/** Notifications */
export const notifications = {
  list: () => apiGet('/notifications'),
  read: (body: JsonMap) => apiPost('/notifications/read', body),
};

/** Quick payment */
export const quickPayment = {
  myQr: () => apiGet('/quick-payment/my-qr'),
  regenerate: () => apiPost('/quick-payment/regenerate'),
  recents: () => apiGet('/quick-payment/recents'),
  resolve: (code: string) => apiGet(`/quick-payment/resolve${q({ code })}`),
  charge: (body: JsonMap) => apiPost('/quick-payment/charge', body),
};

/** Distributor */
export const distributor = {
  packages: () => apiGet('/distributor/recharge/packages'),
  order: (body: JsonMap, idempotencyKey?: string) =>
    apiPost(
      '/distributor/recharge/order',
      body,
      idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    ),
  customers: () => apiGet('/distributor/customers'),
  addCustomer: (body: JsonMap) => apiPost('/distributor/customers/add', body),
  customerProfile: (customerUserId: string | number) =>
    apiGet(`/distributor/customers/profile${q({ customer_user_id: customerUserId })}`),
  updateCustomer: (body: JsonMap) => apiPost('/distributor/customers/update', body),
  topupCustomer: (body: JsonMap) => apiPost('/distributor/customers/topup', body),
  orders: () => apiGet('/distributor/orders'),
  reports: () => apiGet('/distributor/reports/summary'),
};

/** Tahweel / debts / reports / customers (legacy shop tools) */
export const tahweel = {
  customers: () => apiGet('/customers'),
  saveCustomer: (body: JsonMap) => apiPost('/customers', body),
  deleteCustomer: (body: JsonMap) => apiPost('/customers/delete', body),
  statement: (id: string | number) => apiGet(`/customers/statement${q({ id })}`),
  debts: () => apiGet('/tahweel/debts'),
  saveDebt: (body: JsonMap) => apiPost('/tahweel/debts', body),
  settleDebt: (body: JsonMap) => apiPost('/tahweel/debts/settle', body),
  reportsSummary: () => apiGet('/tahweel/reports/summary'),
  prices: () => apiGet('/tahweel/prices'),
  operations: () => apiGet('/tahweel/operations'),
};

/** Settings (Syria API / USSD local) */
export const settings = {
  apiSyria: () => apiGet('/settings/api-syria'),
  saveApiSyriaToken: (body: JsonMap) => apiPost('/settings/api-syria/save-token', body),
  syncAccounts: () => apiPost('/settings/api-syria/sync-accounts'),
  savePin: (body: JsonMap) => apiPost('/settings/accounts/save-pin', body),
  toggleActive: (body: JsonMap) => apiPost('/settings/accounts/toggle-active', body),
};

/** Admin */
export const admin = {
  dashboard: () => apiGet('/admin/dashboard'),
  users: (params?: Record<string, string | number>) => apiGet(`/admin/users${q(params)}`),
  saveUser: (body: JsonMap) => apiPost('/admin/users/save', body),
  userPassword: (body: JsonMap) => apiPost('/admin/users/password', body),
  userStatus: (body: JsonMap) => apiPost('/admin/users/status', body),
  userApiAccess: (body: JsonMap) => apiPost('/admin/users/api-access', body),
  deleteUser: (body: JsonMap) => apiPost('/admin/users/delete', body),
  walletAdjust: (body: JsonMap) => apiPost('/admin/users/wallet-adjust', body),

  deposits: (params?: Record<string, string | number>) => apiGet(`/admin/deposits${q(params)}`),
  reviewDeposit: (body: JsonMap) => apiPost('/admin/deposits/review', body),

  paymentMethods: () => apiGet('/admin/payment/methods'),
  savePaymentMethod: (body: JsonMap) => apiPost('/admin/payment/methods/save', body),
  deletePaymentMethod: (body: JsonMap) => apiPost('/admin/payment/methods/delete', body),

  orders: (params?: Record<string, string | number>) => apiGet(`/admin/orders${q(params)}`),
  productsOrders: (params?: Record<string, string | number>) =>
    apiGet(`/admin/products/orders${q(params)}`),
  productsOrdersCheck: (body: JsonMap) => apiPost('/admin/products/orders/check', body),
  productsOrdersRetry: (body: JsonMap) => apiPost('/admin/products/orders/retry', body),
  productsOrdersResolve: (body: JsonMap) => apiPost('/admin/products/orders/resolve', body),
  productsOrdersResponse: (body: JsonMap) => apiPost('/admin/products/orders/response', body),

  productsLocal: () => apiGet('/admin/products/local'),
  productsExternal: () => apiGet('/admin/products/external'),
  productsSettings: () => apiGet('/admin/products/settings'),
  saveProductsSettings: (body: JsonMap) => apiPost('/admin/products/settings', body),
  productsImport: (body: JsonMap) => apiPost('/admin/products/import', body),
  productsSyncPrices: () => apiPost('/admin/products/sync-prices'),
  productsProfile: (params?: Record<string, string | number>) =>
    apiGet(`/admin/products/profile${q(params)}`),
  categorySave: (body: JsonMap) => apiPost('/admin/products/category/save', body),
  categoryDelete: (body: JsonMap) => apiPost('/admin/products/category/delete', body),
  productSave: (body: JsonMap) => apiPost('/admin/products/save', body),
  productDelete: (body: JsonMap) => apiPost('/admin/products/delete', body),
  manualSave: (body: JsonMap) => apiPost('/admin/products/manual/save', body),
  apiProductSave: (body: JsonMap) => apiPost('/admin/products/api/save', body),
  productsRelink: (body: JsonMap) => apiPost('/admin/products/relink', body),

  rechargePackages: () => apiGet('/admin/recharge/packages'),
  saveRechargePackage: (body: JsonMap) => apiPost('/admin/recharge/packages', body),
  deleteRechargePackage: (body: JsonMap) => apiPost('/admin/recharge/packages/delete', body),
  serviceControls: () => apiGet('/admin/recharge/service-controls'),
  saveServiceControls: (body: JsonMap) => apiPost('/admin/recharge/service-controls', body),
  shamBalanceSettings: () => apiGet('/admin/sham-balance/settings'),
  saveShamBalanceSettings: (body: JsonMap) => apiPost('/admin/sham-balance/settings', body),
  shamBalanceProfile: () => apiGet('/admin/sham-balance/profile'),
  shamBalanceSync: () => apiPost('/admin/sham-balance/sync'),
  rechargeRetry: (body: JsonMap) => apiPost('/admin/recharge/orders/retry', body),
  rechargeReject: (body: JsonMap) => apiPost('/admin/recharge/orders/reject', body),
  rechargeMarkSuccess: (body: JsonMap) => apiPost('/admin/recharge/orders/mark-success', body),

  executorDevices: () => apiGet('/admin/executor/devices'),
  saveExecutorDevice: (body: JsonMap) => apiPost('/admin/executor/device/save', body),
  deleteExecutorDevice: (body: JsonMap) => apiPost('/admin/executor/device/delete', body),
  saveExecutorSim: (body: JsonMap) => apiPost('/admin/executor/sim/save', body),
  executorMessages: (params?: Record<string, string | number>) =>
    apiGet(`/admin/executor/messages${q(params)}`),

  ussdCodes: () => apiGet('/admin/ussd-codes'),
  saveUssdCode: (body: JsonMap) => apiPost('/admin/ussd-codes/save', body),
  deleteUssdCode: (body: JsonMap) => apiPost('/admin/ussd-codes/delete', body),
  ussdServices: () => apiGet('/admin/ussd-services'),
  saveUssdService: (body: JsonMap) => apiPost('/admin/ussd-services/save', body),
  deleteUssdService: (body: JsonMap) => apiPost('/admin/ussd-services/delete', body),
  ussdRules: () => apiGet('/admin/ussd-response-rules'),
  saveUssdRule: (body: JsonMap) => apiPost('/admin/ussd-response-rules/save', body),
  deleteUssdRule: (body: JsonMap) => apiPost('/admin/ussd-response-rules/delete', body),

  billsCategories: () => apiGet('/admin/bills/categories'),
  saveBillsCategory: (body: JsonMap) => apiPost('/admin/bills/categories/save', body),
  deleteBillsCategory: (body: JsonMap) => apiPost('/admin/bills/categories/delete', body),
  billsProducts: () => apiGet('/admin/bills/products'),
  saveBillsProduct: (body: JsonMap) => apiPost('/admin/bills/products/save', body),
  deleteBillsProduct: (body: JsonMap) => apiPost('/admin/bills/products/delete', body),
  billsOrders: (params?: Record<string, string | number>) =>
    apiGet(`/admin/bills/orders${q(params)}`),
  resolveBillsOrder: (body: JsonMap) => apiPost('/admin/bills/orders/resolve', body),

  memberships: () => apiGet('/admin/memberships'),
  saveMembership: (body: JsonMap) => apiPost('/admin/memberships/save', body),
  deleteMembership: (body: JsonMap) => apiPost('/admin/memberships/delete', body),
  membershipPricing: (params?: Record<string, string | number>) =>
    apiGet(`/admin/membership-pricing${q(params)}`),
  saveMembershipPricing: (body: JsonMap) => apiPost('/admin/membership-pricing/save', body),

  siteSettings: () => apiGet('/admin/site-settings'),
  saveSiteSettings: (body: JsonMap) => apiPost('/admin/site-settings', body),
  slides: () => apiGet('/admin/slides'),
  saveSlide: (body: JsonMap) => apiPost('/admin/slides/save', body),
  deleteSlide: (body: JsonMap) => apiPost('/admin/slides/delete', body),
  uploadImage: (body: JsonMap) => apiPost('/admin/upload-image', body),

  financialSummary: (params?: Record<string, string | number>) =>
    apiGet(`/admin/financial/summary${q(params)}`),
  financialLedger: (params?: Record<string, string | number>) =>
    apiGet(`/admin/financial/ledger${q(params)}`),

  securityAudit: (params?: Record<string, string | number>) =>
    apiGet(`/admin/security/audit${q(params)}`),
  compliance: () => apiGet('/admin/compliance'),
  reviewCompliance: (body: JsonMap) => apiPost('/admin/compliance/review', body),
  createStaff: (body: JsonMap) => apiPost('/admin/staff/create', body),
  sendNotification: (body: JsonMap) => apiPost('/admin/notifications/send', body),
  walletTransactions: (params?: Record<string, string | number>) =>
    apiGet(`/admin/wallet-transactions${q(params)}`),
};
